/**
 * Анкета на доступ в кабинет — публичная страница: у человека ещё нет ни учётной записи, ни
 * пароля. Ответ бэка на дубликаты и на уже выданный доступ ОДИНАКОВ, поэтому экран всегда
 * показывает один и тот же текст «заявка принята»: по разнице ответов снаружи можно было бы
 * перебором узнать, у кого есть доступ к этому ВÖİN.
 */
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'

import { api } from '../../src/api/client'
import { errorText } from '../../src/features/edo/errorText'
import { useApp } from '../../src/i18n'
import { tone } from '../../src/theme/tones'
import { Button, FormInput, Seg } from '../../src/ui'

const VOEN_RE = /^\d{10}$/

export default function CabinetSignupScreen() {
  const { t, l } = useApp()
  const router = useRouter()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [voen, setVoen] = useState('')
  const [role, setRole] = useState<'SIGNER' | 'SPECIALIST'>('SPECIALIST')
  const [localError, setLocalError] = useState('')

  const send = useMutation({
    mutationFn: () =>
      api.cabinetSignup({
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        voen: voen.trim(),
        role,
      }),
  })

  const submit = () => {
    setLocalError('')
    if (!fullName.trim()) return setLocalError(l('cab.signup.fullname'))
    if (!email.includes('@')) return setLocalError(l('cab.signup.email'))
    if (!VOEN_RE.test(voen.trim())) return setLocalError(l('cab.signup.voenhint'))
    send.mutate()
  }

  const error =
    localError || (send.error instanceof Error ? t(errorText(send.error)) : '')

  if (send.isSuccess) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding={24} backgroundColor="$bgLayout">
        <YStack width="100%" maxWidth={460} gap={14}>
          <Text fontSize={20} fontWeight="700" color="$color">
            {l('cab.signup.title')}
          </Text>
          <Text fontSize={13} color="$textMuted">
            {l('cab.signup.done')}
          </Text>
          <Button block onPress={() => router.replace('/cabinet/login')}>
            {l('cab.signup.login')}
          </Button>
        </YStack>
      </YStack>
    )
  }

  return (
    <YStack flex={1} alignItems="center" justifyContent="center" padding={24} backgroundColor="$bgLayout">
      <YStack width="100%" maxWidth={460} gap={12}>
        <Text fontSize={20} fontWeight="700" color="$color">
          {l('cab.signup.title')}
        </Text>
        <Text fontSize={13} color="$textMuted">
          {l('cab.signup.hint')}
        </Text>

        <YStack gap={4}>
          <Text fontSize={12} color="$textMuted">{l('cab.signup.fullname')}</Text>
          <FormInput value={fullName} onChangeText={setFullName} autoComplete="name" />
        </YStack>
        <YStack gap={4}>
          <Text fontSize={12} color="$textMuted">{l('cab.signup.email')}</Text>
          <FormInput
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            autoComplete="email"
          />
        </YStack>
        <YStack gap={4}>
          <Text fontSize={12} color="$textMuted">{l('cab.signup.phone')}</Text>
          <FormInput value={phone} onChangeText={setPhone} keyboardType="phone-pad" />
        </YStack>
        <YStack gap={4}>
          <Text fontSize={12} color="$textMuted">{l('cab.signup.voen')}</Text>
          <FormInput value={voen} onChangeText={setVoen} keyboardType="number-pad" maxLength={10} />
          <Text fontSize={11} color="$textMuted">{l('cab.signup.voenhint')}</Text>
        </YStack>

        <YStack gap={6}>
          <Text fontSize={12} color="$textMuted">{l('cab.signup.role')}</Text>
          <XStack gap={8} flexWrap="wrap">
            <Seg
              active={role === 'SPECIALIST'}
              label={l('cab.signup.role.specialist')}
              onPress={() => setRole('SPECIALIST')}
            />
            <Seg
              active={role === 'SIGNER'}
              label={l('cab.signup.role.signer')}
              onPress={() => setRole('SIGNER')}
            />
          </XStack>
        </YStack>

        {!!error && (
          <Text fontSize={13} color={tone('red').text}>
            {error}
          </Text>
        )}

        <Button variant="primary" block disabled={send.isPending} onPress={submit}>
          {send.isPending ? l('cab.signup.sending') : l('cab.signup.send')}
        </Button>
        <Button block onPress={() => router.replace('/cabinet/login')}>
          {l('cab.signup.login')}
        </Button>
      </YStack>
    </YStack>
  )
}
